import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import uniq from "lodash/uniq";
import type { ProductData } from "~/domains/product/types/ProductData";
import { fetchTopsellerProductsThunk } from "~/domains/product/redux/topsellerProducts/actions";

export function useMultipleTopsellerProducts(productCodes: string[]): {
  state: "loaded" | "loading";
  topsellerProducts: ProductData[];
} {
  const dispatch = useDispatch();
  const codes = useSelector(selectMultipleTopsellerProductCodes(productCodes), shallowEqual);
  const isLoaded = codes.every((item) => item?.state === "loaded") ? "loaded" : "loading";
  const uniqueCodes = uniq(codes.map((code) => code?.data).flat());
  const topsellerProducts = useSelector(selectTopsellerProducts(uniqueCodes as string[]), shallowEqual);

  useEffect(() => {
    productCodes.forEach((productCode) => {
      dispatch(fetchTopsellerProductsThunk(productCode));
    });
  }, []);

  return useMemo(() => {
    return { state: isLoaded, topsellerProducts: topsellerProducts };
  }, [codes, topsellerProducts]);
}

export function useTopsellerProducts(productCode: string) {
  const dispatch = useDispatch();
  const topsellerProductCodes = useSelector(selectTopsellerProductCodes(productCode));
  const topsellerProducts = useSelector(selectTopsellerProducts(topsellerProductCodes?.data ?? []), shallowEqual);

  useEffect(() => {
    if (!topsellerProductCodes) {
      dispatch(fetchTopsellerProductsThunk(productCode));
    }
  }, [productCode, topsellerProductCodes]);

  return useMemo(() => {
    return { state: topsellerProductCodes?.state ?? "loading", topsellerProducts: topsellerProducts ?? [] };
  }, [topsellerProductCodes, topsellerProducts]);
}

const selectTopsellerProductCodes = (productCode: string) => (state: RootState) =>
  state.products.topsellerProducts[productCode];

const selectMultipleTopsellerProductCodes = (productCodes: string[]) => (state: RootState) => {
  return productCodes.map((productCode) => state.products.topsellerProducts[productCode]);
};

const selectTopsellerProducts = (productCodes: string[]) => (state: RootState) =>
  productCodes.map((c) => state.products.basic[c]).filter(Boolean);
